import { FireElement, LeafElement, WaterElement } from 'assets/game/elements'
import { BlackOrb, BlackOrbKing, WhiteOrb, WhiteOrbKing } from 'assets/game/orbs'
import { Wall } from 'assets/game/wall'

import { HexBoard, HexId, HexPiece } from 'features/hex-board/hex-board'
import { getNeighborPieces, getNeighbors } from 'features/hex-board/utils'

import { getEntries, getKeys } from 'shared/utils'

import { BlackPiece, Hand, KingPiece, NonKingPiece, Piece, PlayerColor, WhitePiece } from './board'

export type ValidWallPositions = '-1_-1' | '0_-2' | '1_-3' | '3_-3' | '3_-2' | '3_-1'
export type ValidSkewedWallPositions = '2_1' | '1_2' | '0_3' | '-2_3' | '-2_2' | '-2_1'

export const allowedInitialHexes: Record<PlayerColor, HexId[]> = {
  white: ['-2_1', '-1_1', '0_1', '1_1', '2_1', '-2_2', '-1_2', '0_2', '1_2', '-2_3', '-1_3', '0_3'],
  black: ['-1_-1', '0_-1', '1_-1', '2_-1', '3_-1', '0_-2', '1_-2', '2_-2', '3_-2', '1_-3', '2_-3', '3_-3'],
}

export const firstLineHexes: Record<PlayerColor, HexId[]> = {
  white: ['-2_1', '-1_1', '0_1', '1_1', '2_1'],
  black: ['-1_-1', '0_-1', '1_-1', '2_-1', '3_-1'],
}

const allowedPieces: Record<PlayerColor, HexPiece[]> = {
  white: ['w', 'l', 'f', 'kw', 'kf', 'kl'],
  black: ['W', 'L', 'F', 'kW', 'kF', 'kL'],
}

export const mirroredPositions: Record<ValidWallPositions, ValidSkewedWallPositions[]> = {
  '-1_-1': ['-2_1', '2_1'],
  '0_-2': ['-2_2', '1_2'],
  '1_-3': ['-2_3', '0_3'],
  '3_-1': ['-2_1', '2_1'],
  '3_-2': ['-2_2', '1_2'],
  '3_-3': ['-2_3', '0_3'],
}

const whichKills: Record<Piece, Piece[]> = {
  f: ['F', 'W', 'kF', 'kW'],
  kf: ['F', 'W', 'kF', 'kW'],
  w: ['W', 'L', 'kW', 'kL'],
  kw: ['W', 'L', 'kW', 'kL'],
  l: ['F', 'L', 'kF', 'kL'],
  kl: ['F', 'L', 'kF', 'kL'],
  F: ['f', 'w', 'kf', 'kw'],
  kF: ['f', 'w', 'kf', 'kw'],
  W: ['w', 'l', 'kw', 'kl'],
  kW: ['w', 'l', 'kw', 'kl'],
  L: ['f', 'l', 'kf', 'kl'],
  kL: ['f', 'l', 'kf', 'kl'],
}

export const playerOrbs = {
  white: {
    king: WhiteOrbKing,
    l: WhiteOrb,
    w: WhiteOrb,
    f: WhiteOrb,
  },
  black: {
    king: BlackOrbKing,
    L: BlackOrb,
    W: BlackOrb,
    F: BlackOrb,
  },
}

export const isEmptyHex = (hexPiece: HexPiece) => hexPiece === null

export const oppositePlayer = (player: PlayerColor) => (player === 'black' ? 'white' : 'black')

export const generateWallPositions = () => {
  const wallIdx = Math.floor(Math.random() * getKeys(mirroredPositions).length)
  const mirroredWallIdx = Math.floor(Math.random() * 2)

  const wallHexId = getKeys(mirroredPositions)[wallIdx]
  const mirroredWallHexId = Object.values(mirroredPositions)[wallIdx][mirroredWallIdx]

  return [wallHexId, mirroredWallHexId] as const
}

export const getNonKingPiece = (piece: Piece): WhitePiece | BlackPiece =>
  isKingPiece(piece) ? (piece.slice(1) as NonKingPiece) : (piece as NonKingPiece)

export const removePieceFromHand = (hand: Hand, piece: Piece) => {
  const pieceIdx = hand.indexOf(getNonKingPiece(piece))

  if (pieceIdx !== -1) {
    return [...hand.slice(0, pieceIdx), ...hand.slice(pieceIdx + 1)]
  }

  return hand
}

export const isMyPiece = (player: PlayerColor, piece: HexPiece) => {
  if (!piece) return false

  return allowedPieces[player].includes(piece)
}

export const isWallPiece = (piece: HexPiece): piece is 'wall' => piece === 'wall'
export const isPiece = (hexPiece: HexPiece): hexPiece is Piece => Boolean(hexPiece && !isWallPiece(hexPiece))

export const isFirePiece = (piece: HexPiece) => isPiece(piece) && ['f', 'F', 'kf', 'kF'].includes(piece)
export const isWaterPiece = (piece: HexPiece) => isPiece(piece) && ['w', 'W', 'kw', 'kW'].includes(piece)
export const isLeafPiece = (piece: HexPiece) => isPiece(piece) && ['l', 'L', 'kl', 'kL'].includes(piece)

export const isBlackPiece = (piece: HexPiece): piece is BlackPiece => isPiece(piece) && ['W', 'L', 'F'].includes(piece)
export const isWhitePiece = (piece: HexPiece): piece is WhitePiece => isPiece(piece) && ['w', 'l', 'f'].includes(piece)

export const isBlackKingPiece = (piece: HexPiece): piece is KingPiece => isPiece(piece) && /^k[WFL]$/.test(piece)
export const isWhiteKingPiece = (piece: HexPiece): piece is KingPiece => isPiece(piece) && /^k[wfl]$/.test(piece)
export const isKingPiece = (piece: HexPiece) => isBlackKingPiece(piece) || isWhiteKingPiece(piece)

export const handHasFire = (hand: Hand) => hand.includes('f') || hand.includes('F')
export const handHasWater = (hand: Hand) => hand.includes('w') || hand.includes('W')
export const handHasLeaf = (hand: Hand) => hand.includes('l') || hand.includes('L')

export const isAlly = (piece: HexPiece, playerColor: PlayerColor) => {
  if (playerColor === 'white') {
    if (isWhiteKingPiece(piece) || isWhitePiece(piece)) return true

    return false
  } else {
    if (isBlackKingPiece(piece) || isBlackPiece(piece)) return true

    return false
  }
}

export const getPieceComponent = (piece: HexPiece) => {
  if (!piece) return null

  if (isWallPiece(piece)) {
    return Wall
  }

  if (isFirePiece(piece)) {
    return FireElement
  }

  if (isWaterPiece(piece)) {
    return WaterElement
  }

  if (isLeafPiece(piece)) {
    return LeafElement
  }

  return null
}

export const getOrbComponent = (piece: HexPiece, className: string) => {
  if (!piece) return null

  if (isWallPiece(piece)) {
    return <Wall />
  }

  if (isBlackKingPiece(piece)) {
    const Comp = playerOrbs.black.king
    return <Comp className={className} size={220} />
  }

  if (isBlackPiece(piece)) {
    const Comp = playerOrbs.black[piece]
    return <Comp className={className} />
  }

  if (isWhiteKingPiece(piece)) {
    const Comp = playerOrbs.white.king
    return <Comp className={className} size={220} />
  }

  if (isWhitePiece(piece)) {
    const Comp = playerOrbs.white[piece]
    return <Comp className={className} />
  }

  return null
}

export const isHexValidForDistribute = (player: PlayerColor, hexId: HexId) =>
  allowedInitialHexes[player].includes(hexId)

export const isHexValidForDeploy = (player: PlayerColor, hexId: HexId, hexBoard: HexBoard) => {
  if (!isEmptyHex(hexBoard[hexId])) return false

  const neighbors = getNeighbors(hexId)
  const isAllyInNeighborhood = neighbors.some(neighbor => isMyPiece(player, hexBoard[neighbor]))

  return isAllyInNeighborhood
}

export const killsMe = (me: HexPiece, other: HexPiece): boolean => {
  if (!me || isWallPiece(me) || !other || isWallPiece(other)) return false

  return whichKills[me].includes(other)
}

export const someNeighborsKillsHex = (hexId: HexId, hexBoard: HexBoard) => {
  const piece = hexBoard[hexId]

  if (!isPiece(piece)) return false

  const neighbors = getNeighborPieces(hexId, hexBoard)

  return neighbors.some(neighbor => killsMe(piece, neighbor))
}

export const resolveConflicts = (hexBoard: HexBoard): HexBoard => {
  const deadPieces: Partial<HexBoard> = {}

  getEntries(hexBoard).forEach(([hexId, piece]) => {
    if (!isPiece(piece)) return

    if (someNeighborsKillsHex(hexId, hexBoard)) {
      deadPieces[hexId] = null
    }
  })

  return {
    ...hexBoard,
    ...deadPieces,
  }
}
